export const formatMoney = (value, currency = "EUR") => {
  const num = Number(value || 0);
  try {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${num.toFixed(2)} ${currency || ""}`.trim();
  }
};

export const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export const imgSrc = (base64, mime = "image/jpeg") =>
  base64 ? `data:${mime};base64,${base64}` : "";

// When a dropdown value is "Sonstiges" and a free-text is given, prefer it.
export const effectiveValue = (value, other) => {
  if (value === "Sonstiges" && (other || "").trim()) {
    return `Sonstiges (${other.trim()})`;
  }
  return value || "";
};

export const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// Sort a list of receipts by a key ("date" | "supplier" | "purpose" | "price")
// and direction ("asc" | "desc"). Returns a new array (stable; equal items keep
// their incoming order, which is newest-created-first from the backend).
export const sortReceipts = (list, key = "date", dir = "desc") => {
  const factor = dir === "asc" ? 1 : -1;
  const valueOf = (r) => {
    switch (key) {
      case "supplier":
        return (r.supplier || "").toLowerCase();
      case "purpose":
        return effectiveValue(r.purpose, r.purpose_other).toLowerCase();
      case "price":
        return Number(r.price || 0);
      case "date":
      default:
        return r.date || "";
    }
  };
  return [...list].sort((a, b) => {
    const va = valueOf(a);
    const vb = valueOf(b);
    if (typeof va === "number" && typeof vb === "number") {
      return (va - vb) * factor;
    }
    return String(va).localeCompare(String(vb), "de") * factor;
  });
};

// Group receipts by currency, returning rounded gross/VAT totals per currency.
// EUR is listed first, the rest alphabetically. Returns at least one entry (EUR).
export const sumByCurrency = (list) => {
  const by = {};
  for (const r of list) {
    const cur = r.currency || "EUR";
    if (!by[cur]) by[cur] = { currency: cur, total: 0, vat: 0, tip: 0 };
    by[cur].total += Number(r.price || 0);
    by[cur].vat += Number(r.vat_amount || 0);
    by[cur].tip += Number(r.tip || 0);
  }
  const round2 = (n) => Math.round(n * 100) / 100;
  const rows = Object.values(by).map((x) => ({
    currency: x.currency,
    total: round2(x.total),
    vat: round2(x.vat),
    tip: round2(x.tip),
    grandTotal: round2(x.total + x.tip),
  }));
  rows.sort((a, b) => {
    if (a.currency === b.currency) return 0;
    if (a.currency === "EUR") return -1;
    if (b.currency === "EUR") return 1;
    return a.currency.localeCompare(b.currency);
  });
  return rows.length
    ? rows
    : [{ currency: "EUR", total: 0, vat: 0, tip: 0, grandTotal: 0 }];
};
